import React, { forwardRef } from "react";
import { useFormContext, RegisterOptions } from "react-hook-form";
import cn from "classnames";
import { useMergeRefs } from "storefront/hooks/useMergeRefs";
import { useFormControl } from "../FormControl";
import styles from "./Input.module.scss";

interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "name"> {
  rules?: RegisterOptions;
  className?: string;
}

/**
 * Input component
 *
 * A form input component that integrates with react-hook-form.
 * Automatically registers with the form and displays validation states.
 */
const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ rules, className, disabled, type = "text", ...props }, ref) => {
    const {
      register,
      formState: { isSubmitting },
    } = useFormContext();
    const { name, error } = useFormControl();

    const { ref: hookFormRef, ...registerProps } = register(name, rules);

    const mergedRef = useMergeRefs(hookFormRef, ref);

    return (
      <input
        id={name}
        ref={mergedRef}
        type={type}
        className={cn(
          styles.input,
          { [styles.hasError]: !!error },
          { [styles.disabled]: disabled || isSubmitting },
          className,
        )}
        disabled={disabled || isSubmitting}
        {...registerProps}
        {...props}
      />
    );
  },
);

Input.displayName = "Input";

export default Input;
